import React from 'react'
import type { JSX } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { usePermissions } from '@/hooks/usePermissions'
import { Gauge, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

/** A nav dropdown that several features' items collapse into. Keyed by label. */
export interface NavGroupConfig {
  label: string
  icon?: LucideIcon
}

/**
 * Declared once and imported by every feature that belongs to it, so the features can't
 * drift on the label or icon. Each feature still owns its own nav item — that way a
 * feature that gets disabled takes its entry out of the menu with it, rather than leaving
 * behind a link to a route that no longer exists.
 */
export const USAGE_NAV_GROUP: NavGroupConfig = { label: 'Usage', icon: Gauge }

export interface NavItemConfig {
  path: string
  label: string
  icon?: LucideIcon
  /** Collapse into a shared dropdown with every other item declaring the same group. */
  group?: NavGroupConfig
  /** Required permissions (supports wildcards: 'admin:*', '*:*') */
  requiredPermissions?: string[]
  /** @deprecated Use requiredPermissions instead */
  requiredScopes?: string[]
}

/** The permission gate, shared by NavItem and NavMenu so the two can't diverge. */
export function navItemPermissions(item: NavItemConfig): string[] | undefined {
  return item.requiredPermissions ?? item.requiredScopes
}

interface NavItemProps {
  item: NavItemConfig
  collapsed?: boolean
}

export const NavItem = ({ item, collapsed = false }: NavItemProps): JSX.Element | null => {
  const location = useLocation()
  const { hasAnyPermission } = usePermissions()

  // Support both new requiredPermissions and legacy requiredScopes
  const required = navItemPermissions(item)
  if (required && required.length > 0 && !hasAnyPermission(required)) {
    return null
  }

  const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  const Icon = item.icon

  return (
    <Link to={item.path}>
      <Button
        variant={isActive ? 'secondary' : 'ghost'}
        size="sm"
        className={cn(
          'w-full justify-start gap-2',
          isActive && 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
        )}
      >
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        {!collapsed && <span>{item.label}</span>}
      </Button>
    </Link>
  )
}
